import {
  Download,
  Ellipsis,
  Loader2,
  Lock,
  MessageCircle,
  MessageSquareText,
  SquarePen,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import type { PayPeriodLockTarget } from "@/payroll/pay-periods/pay-period-lock";
import { PayPeriodLockDialog } from "@/payroll/pay-periods/pay-period-lock-action";
import {
  isPayBreakdownRerunMenuActionVisible,
  type PayBreakdownRerunMenuAction,
  PayBreakdownRerunMenuItem,
} from "./pay-breakdown-rerun-action";
import type { PayBreakdownDownloadAction } from "./types";

interface PayBreakdownMoreActionsMenuProps {
  download?: PayBreakdownDownloadAction;
  onOpenManualEdits?: () => void;
  onOpenComments?: () => void;
  onOpenExplanationChat?: () => void;
  rerun?: PayBreakdownRerunMenuAction;
  // Closing out the period. Terminal, once per period, and only Volca can undo
  // it — so it sits with the other occasional period actions rather than
  // holding a button in the header.
  lock?: PayPeriodLockTarget;
}

export const PayBreakdownMoreActionsMenu = ({
  download,
  onOpenManualEdits,
  onOpenComments,
  onOpenExplanationChat,
  rerun,
  lock,
}: PayBreakdownMoreActionsMenuProps) => {
  const [isLockDialogOpen, setIsLockDialogOpen] = useState(false);
  const hasRerun = isPayBreakdownRerunMenuActionVisible(rerun);
  const hasPanels =
    !!onOpenManualEdits || !!onOpenComments || !!onOpenExplanationChat;
  const hasActions = !!download || hasPanels || hasRerun || !!lock;

  if (!hasActions) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            size="icon"
            aria-label="More actions"
            aria-busy={download?.isDownloading}
            leftIcon={
              download?.isDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Ellipsis className="h-4 w-4" />
              )
            }
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-56">
          {download && (
            <DropdownMenuItem
              disabled={download.isDownloading}
              onSelect={download.onDownload}
            >
              {download.isDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Download XLSX
            </DropdownMenuItem>
          )}
          {download && hasRerun && <DropdownMenuSeparator />}
          {rerun && <PayBreakdownRerunMenuItem rerun={rerun} />}
          {hasPanels && (download || hasRerun) && <DropdownMenuSeparator />}
          {onOpenExplanationChat && (
            <DropdownMenuItem onSelect={onOpenExplanationChat}>
              <MessageCircle className="h-4 w-4" />
              AI chat
            </DropdownMenuItem>
          )}
          {onOpenManualEdits && (
            <DropdownMenuItem onSelect={onOpenManualEdits}>
              <SquarePen className="h-4 w-4" />
              Manual edits
            </DropdownMenuItem>
          )}
          {onOpenComments && (
            <DropdownMenuItem onSelect={onOpenComments}>
              <MessageSquareText className="h-4 w-4" />
              Comments
            </DropdownMenuItem>
          )}
          {lock && (download || hasRerun || hasPanels) && (
            <DropdownMenuSeparator />
          )}
          {lock && (
            <DropdownMenuItem onSelect={() => setIsLockDialogOpen(true)}>
              <Lock className="h-4 w-4" />
              Lock pay period
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Outside the dropdown on purpose: the menu unmounts its content on
          select, which would take the dialog with it. */}
      {lock && (
        <PayPeriodLockDialog
          businessId={lock.businessId}
          period={lock.period}
          open={isLockDialogOpen}
          onOpenChange={setIsLockDialogOpen}
        />
      )}
    </>
  );
};
